import { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SIZE, idx } from './spriteData';

const SCALE = 14; // on-screen px per art pixel
const CHECKER = ['#2a2f3a', '#232733'];

/**
 * The paint grid.
 *
 * A canvas rather than 1,024 divs: dragging across a DOM grid means a
 * mousemove handler per cell and a reconciliation pass per pixel, which is
 * exactly the wrong shape. Here a single element handles pointer events and
 * one imperative redraw covers any change.
 *
 * This does NOT contradict the "React never renders game entities" rule in
 * Play.js. That rule is about frame rate — a 60fps loop must not drive the
 * reconciler. An editor redraws when a human changes something, so React
 * state is the right owner here.
 */
export default function PixelCanvas({
  pixels, color, tool, showGrid, onPaint, onPick,
}) {
  const canvasRef = useRef(null);
  const painting = useRef(false);
  const lastCell = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Checkerboard so transparent reads as transparent, not as black.
    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        ctx.fillStyle = CHECKER[(x + y) % 2];
        ctx.fillRect(x * SCALE, y * SCALE, SCALE, SCALE);
      }
    }

    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        const c = pixels[idx(x, y)];
        if (c) {
          ctx.fillStyle = c;
          ctx.fillRect(x * SCALE, y * SCALE, SCALE, SCALE);
        }
      }
    }

    if (showGrid) {
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 1; i < SIZE; i += 1) {
        ctx.moveTo(i * SCALE + 0.5, 0);
        ctx.lineTo(i * SCALE + 0.5, SIZE * SCALE);
        ctx.moveTo(0, i * SCALE + 0.5);
        ctx.lineTo(SIZE * SCALE, i * SCALE + 0.5);
      }
      ctx.stroke();
    }

    // Centre guides — characters must be horizontally centred in the cell,
    // and feet sit near the bottom. Easy to drift without a reference.
    ctx.strokeStyle = 'rgba(212,162,76,0.35)';
    ctx.beginPath();
    ctx.moveTo((SIZE / 2) * SCALE + 0.5, 0);
    ctx.lineTo((SIZE / 2) * SCALE + 0.5, SIZE * SCALE);
    ctx.moveTo(0, 28 * SCALE + 0.5);
    ctx.lineTo(SIZE * SCALE, 28 * SCALE + 0.5);
    ctx.stroke();
  }, [pixels, showGrid]);

  const cellFrom = useCallback((event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    // Scale by rect/canvas ratio so this stays correct if CSS resizes it.
    const x = Math.floor(((event.clientX - rect.left) / rect.width) * SIZE);
    const y = Math.floor(((event.clientY - rect.top) / rect.height) * SIZE);
    if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) return null;
    return { x, y };
  }, []);

  const apply = useCallback((event) => {
    const cell = cellFrom(event);
    if (!cell) return;

    // Dragging fires many events per cell; skip repeats so we don't rebuild
    // the pixel array for a pointer that hasn't left the square.
    const key = `${cell.x},${cell.y}`;
    if (lastCell.current === key) return;
    lastCell.current = key;

    if (tool === 'picker') {
      onPick(pixels[idx(cell.x, cell.y)]);
      return;
    }
    onPaint(cell.x, cell.y, tool);
  }, [cellFrom, onPaint, onPick, pixels, tool]);

  const handleDown = (event) => {
    event.preventDefault();
    painting.current = true;
    lastCell.current = null;
    apply(event);
  };

  const handleMove = (event) => {
    if (!painting.current) return;
    // Fill and picker are single-shot; dragging them makes no sense.
    if (tool === 'fill' || tool === 'picker') return;
    apply(event);
  };

  const stop = () => {
    painting.current = false;
    lastCell.current = null;
  };

  return (
    <canvas
      ref={canvasRef}
      width={SIZE * SCALE}
      height={SIZE * SCALE}
      className="rounded-md border border-gray-700 cursor-crosshair touch-none"
      style={{ imageRendering: 'pixelated' }}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      aria-label={`Sprite frame editor, ${SIZE} by ${SIZE} pixels, ${color} selected`}
    />
  );
}

PixelCanvas.propTypes = {
  pixels: PropTypes.arrayOf(PropTypes.string).isRequired,
  color: PropTypes.string.isRequired,
  tool: PropTypes.oneOf(['pencil', 'eraser', 'fill', 'picker']).isRequired,
  showGrid: PropTypes.bool.isRequired,
  onPaint: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};
